<form [formGroup]="riskForm" #riskFormDirective="ngForm" (ngSubmit)="onSubmit()">
  @if (!hideInvestmentAmount) {
    <!-- Investment amount -->
    <app-grow-investment-amount
      [parentForm]="riskForm"
      [growAccount]="growAccount"
      [initialInvestmentOptions]="initialInvestment"
      [savingsPlanOptions]="savingsPlan"
      [riskFormSubmitted]="riskFormSubmitted"
    ></app-grow-investment-amount>
  }

  @if (growAccount) {
    <!-- Risk determination -->
    <div class="risk-questions">
      <h3 class="ds-heading-1">{{ 'GROW.riskProfile.profile.title' | translate }}</h3>
      <p class="description">{{ 'GROW.riskProfile.profile.description' | translate }}</p>

      @if (!hideInvestCallout) {
        <app-ds-callout variant="info" [description]="'GROW.riskProfile.profile.3bRiskProfile.disclaimer.description' | translate"></app-ds-callout>
      }

      <div class="risk-question">
        <div class="risk-assets scroll-point" formGroupName="assets">
          <div
            class="ds-form-row"
            [class.has-error]="(monthlyIncomeControl.touched && monthlyIncomeControl.invalid) || (riskFormSubmitted && monthlyIncomeControl.invalid)"
          >
            <h4 class="ds-heading-2">{{ 'GROW.riskProfile.profile.monthlyLabel' | translate: { currency: growAccount.currency } }}</h4>
            @if (incomeEditMode) {
              <input #incomeInputEditable formControlName="income" class="ds-input" type="number" (blur)="incomeEditMode = false" />
            } @else {
              <input
                [value]="!monthlyIncomeControl.value ? '' : (monthlyIncomeControl.value | thousand)"
                class="ds-input"
                readonly
                type="text"
                [placeholder]="6000 | thousand"
                (focus)="enterIncomeEditMode()"
              />
            }

            @if (monthlyIncomeControl.touched && monthlyIncomeControl.invalid) {
              <div class="ds-input-error">
                @if (monthlyIncomeControl.hasError('required')) {
                  {{ 'GROW.riskProfile.profile.errors.required' | translate }}
                }
                @if (monthlyIncomeControl.hasError('min')) {
                  {{ 'GROW.riskProfile.profile.errors.greaterThanZero' | translate }}
                }
              </div>
            }
          </div>

          @if (visibleQuestionIDs.has(AVAILABLE_ASSETS_ID)) {
            <div
              class="ds-form-row"
              [class.has-error]="
                (availableAssetsControl.touched && availableAssetsControl.invalid) ||
                invalidFreely ||
                (riskFormSubmitted && availableAssetsControl.invalid)
              "
            >
              <h4 class="ds-heading-2">{{ 'GROW.riskProfile.profile.freelyLabel' | translate: { currency: growAccount.currency } }}</h4>

              @if (assetsEditMode) {
                <input #assetsInputEditable formControlName="assets" class="ds-input" type="number" (blur)="assetsEditMode = false" />
              } @else {
                <input
                  [value]="!availableAssetsControl.value ? '' : (availableAssetsControl.value | thousand)"
                  class="ds-input"
                  readonly
                  type="text"
                  [placeholder]="100000 | thousand"
                  (focus)="enterAssetsEditMode()"
                />
              }

              @if (availableAssetsControl.touched && availableAssetsControl.invalid) {
                <div class="ds-input-error">
                  @if (availableAssetsControl.hasError('required')) {
                    {{ 'GROW.riskProfile.profile.errors.required' | translate }}
                  }
                  @if (availableAssetsControl.hasError('min')) {
                    {{ 'GROW.riskProfile.profile.errors.greaterThanZero' | translate }}
                  }
                </div>
              }

              @if (invalidFreely) {
                <div class="ds-input-error">
                  {{ 'GROW.riskProfile.profile.errors.lessThanInitialInvestment' | translate }}
                </div>
              }
            </div>
          }
        </div>
      </div>

      <div class="risk-question" formGroupName="survey">
        <!-- Experience question -->
        <ng-container formGroupName="experience">
          @if (visibleQuestionIDs.has(experienceFormGroup.get('id').value)) {
            <app-inyova-grow-single-box
              class="scroll-point"
              formControlName="selectedAnswerId"
              [title]="growRisk.surveyGroups.experience.question_text"
              [hasError]="riskFormSubmitted && experienceFormGroup.invalid"
              [options]="growRisk.surveyGroups.experience.survey_question_options"
            ></app-inyova-grow-single-box>
          }
        </ng-container>
        <!-- Horizon question -->
        <ng-container formGroupName="investHorizon">
          @if (isInvestHorizonQuestionVisible) {
            <app-inyova-grow-investment-horizon-box
              class="scroll-point"
              formControlName="selectedAnswerId"
              [title]="growRisk.surveyGroups.investHorizon.question_text"
              [hint]="growRisk.surveyGroups.investHorizon.hint"
              [hasError]="riskFormSubmitted && investHorizonFormGroup.invalid"
              [showShortHorizonError]="riskFormSubmitted && showShortHorizonError"
              [options]="growRisk.surveyGroups.investHorizon.survey_question_options"
              (investmentHorizonChange)="onInvestHorizonChange($event)"
              (showInfoHandler)="trackTooltip('investHorizon')"
            >
            </app-inyova-grow-investment-horizon-box>
          }
        </ng-container>
        <!-- risk single choice -->
        <ng-container formArrayName="basic">
          @for (item of basicQuestionsArray.controls; track item; let i = $index) {
            <ng-container [formGroupName]="i">
              @if (visibleQuestionIDs.has(item.get('id').value)) {
                <app-inyova-grow-single-box
                  class="scroll-point"
                  formControlName="selectedAnswerId"
                  [chips]="true"
                  [title]="growRisk.surveyGroups.basic[i].question_text"
                  [hint]="growRisk.surveyGroups.basic[i].hint"
                  [hasError]="riskFormSubmitted && item.invalid"
                  [options]="growRisk.surveyGroups.basic[i].survey_question_options"
                  (toggleHintHandler)="trackTooltip(growRisk.surveyGroups.basic[i].question_text)"
                ></app-inyova-grow-single-box>
              }
            </ng-container>
          }
        </ng-container>
        <!-- Optional questions -->
        <h4 class="ds-heading-2">{{ growRisk.surveyGroups.optionalList.question_text }}</h4>
        <div class="question-row" formArrayName="optional">
          @for (item of optionalQuestionsArray?.controls; track item; let i = $index) {
            <ng-container [formGroupName]="i">
              <app-inyova-grow-optional-box
                class="scroll-point"
                [title]="growRisk.surveyGroups.optional[i]?.question_text"
                [chips]="growRisk.surveyGroups.optional[i]?.survey_question_options"
                [checkboxLabel]="growRisk.surveyGroups.optionalList.survey_question_options[i]?.option_text"
                [checkboxChecked]="item.get('selected').value"
                [hasError]="riskFormSubmitted && item.invalid"
                (toggleOptional)="onToggleOptional(i)"
                formControlName="selectedAnswerId"
              ></app-inyova-grow-optional-box>
            </ng-container>
          }
        </div>
        <!-- Single choice -->
        <ng-container formArrayName="single">
          @for (item of singleQuestionsArray.controls; track item; let i = $index) {
            <ng-container [formGroupName]="i">
              @if (visibleQuestionIDs.has(item.get('id').value)) {
                <app-inyova-grow-single-box
                  class="scroll-point"
                  formControlName="selectedAnswerId"
                  [title]="growRisk.surveyGroups.singleChoice[i].question_text"
                  [hasError]="riskFormSubmitted && item.invalid"
                  [options]="growRisk.surveyGroups.singleChoice[i].survey_question_options"
                >
                </app-inyova-grow-single-box>
              }
            </ng-container>
          }
        </ng-container>
        <!-- Multi choice-->
        <ng-container formArrayName="multi">
          @for (item of multiQuestionsArray.controls; track item; let i = $index) {
            <ng-container [formGroupName]="i">
              @if (visibleQuestionIDs.has(item.get('id').value)) {
                <div>
                  <h4 class="ds-heading-2" [class.ds-color-error]="riskFormSubmitted && item.invalid">
                    {{ growRisk.surveyGroups.multiChoices[i].question_text }}
                  </h4>
                  <div class="question-row">
                    <app-inyova-grow-multi-box [options]="growRisk.surveyGroups.multiChoices[i].survey_question_options" [parent]="getFormGroup(i)">
                    </app-inyova-grow-multi-box>
                  </div>
                </div>
              }
            </ng-container>
          }
        </ng-container>
      </div>
    </div>
  }

  @if (riskForm.valid && riskPercentages) {
    <app-inyova-grow-risk-slider
      [acceptConsent]="growAccount.investment_risk_consent"
      [acceptFees]="growAccount.fees_consent"
      [editGrowRisk]="editGrowRisk"
      [initialInvestment]="initInvestControl.value"
      [currency]="growAccount.currency"
      [showConsentError]="riskFormSubmitted && showConsentError"
      [showFeesError]="riskFormSubmitted && showFeesError"
      [selectedRiskSplit]="growAccount.stock_split"
      [suggestedRiskSplit]="riskPercentages.recommended_projects_percentage"
      [marketFundRate]="riskPercentages.money_market_interest"
      [minReturnPercentage]="riskPercentages.minimum_interest"
      [maxReturnPercentage]="riskPercentages.maximum_interest"
      (riskChange)="onRiskChange($event)"
      [hideCTA]="hideCTA"
    ></app-inyova-grow-risk-slider>
  } @else {
    @if (!hideCTA) {
      <div class="risk-actions">
        <app-ds-button type="submit">{{ (editGrowRisk ? 'GROW.riskProfile.UpdateCTA' : 'GROW.riskProfile.CTA') | translate }}</app-ds-button>
      </div>
    }
  }
</form>
